import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { auditLogsApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search,
  RefreshCw,
  Filter,
  Download,
  LogIn,
  Plus,
  CheckCircle2,
  XCircle,
  Clock,
  Edit,
  Trash2,
  Mail,
  MessageSquare
} from 'lucide-react';

const ACTIONS = [
  { value: 'LOGIN', label: 'Connexion', icon: LogIn, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'CREATE', label: 'Création', icon: Plus, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'EXECUTE', label: 'Exécution', icon: CheckCircle2, color: 'bg-green-50 text-green-700 border-green-200' },
  { value: 'REJECT', label: 'Rejet', icon: XCircle, color: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'SET_PENDING', label: 'Mise en attente', icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'UPDATE', label: 'Modification', icon: Edit, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { value: 'DELETE', label: 'Suppression', icon: Trash2, color: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'SEND_EMAIL', label: 'Email envoyé', icon: Mail, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { value: 'SEND_SMS', label: 'SMS envoyé', icon: MessageSquare, color: 'bg-teal-50 text-teal-700 border-teal-200' },
];

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [searchEmail, setSearchEmail] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (actionFilter && actionFilter !== 'all') params.action = actionFilter;
      if (searchEmail) params.admin_email = searchEmail;
      
      const response = await auditLogsApi.getAll(params);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, searchEmail]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionBadge = (action) => {
    const actionConfig = ACTIONS.find(a => a.value === action);
    if (!actionConfig) {
      return <Badge variant="outline" className="rounded-full text-xs">{action}</Badge>;
    }
    const Icon = actionConfig.icon;
    return (
      <Badge variant="outline" className={`${actionConfig.color} rounded-full text-xs flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {actionConfig.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const exportToPDF = () => {
    const printContent = document.getElementById('audit-table');
    const printWindow = window.open('', '', 'width=900,height=600');
    printWindow.document.write('<html><head><title>Journal d\'Audit - WorldPay</title>');
    printWindow.document.write('<style>table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ccc;padding:8px;text-align:left;font-size:12px;}th{background:#f5f5f5;}</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write('<h1>Journal d\'Audit - WorldPay</h1>');
    printWindow.document.write('<p>Date d\'export: ' + new Date().toLocaleString('fr-FR') + '</p>');
    printWindow.document.write(printContent?.innerHTML || '');
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="audit-logs-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl text-slate-900">Journal d'Audit</h1>
            <p className="text-slate-500 text-sm mt-1">Traçabilité complète des actions administrateur</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-slate-200 shadow-sm rounded-lg">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par email admin..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="pl-10 rounded-lg"
                  data-testid="search-admin-input"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger className="w-full sm:w-48 rounded-lg" data-testid="action-filter">
                    <Filter className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Type d'action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les actions</SelectItem>
                    {ACTIONS.map(a => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button variant="outline" className="rounded-lg flex-1 sm:flex-none" onClick={exportToPDF} data-testid="export-audit-btn">
                    <Download className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Export PDF</span>
                  </Button>
                  <Button variant="outline" className="rounded-lg flex-1 sm:flex-none" onClick={fetchLogs} data-testid="refresh-audit-btn">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mobile Cards View */}
        <div className="block md:hidden space-y-3">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#0066FF]" />
            </div>
          ) : logs.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="text-center py-12 text-slate-400">
                Aucun log trouvé
              </CardContent>
            </Card>
          ) : (
            logs.map((log) => (
              <Card key={log.id} className="border-slate-200 shadow-sm">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-slate-500">{formatDate(log.timestamp)}</p>
                      <p className="font-medium text-sm text-slate-900">{log.admin_email}</p>
                    </div>
                    {getActionBadge(log.action)}
                  </div>
                  <p className="text-sm text-slate-600 break-words">{log.details}</p>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">{log.entity_type}</span>
                    <span className="font-mono">{log.ip_address || '-'}</span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Desktop Logs Table */}
        <Card className="border-slate-200 shadow-sm rounded-lg overflow-hidden hidden md:block">
          <div className="overflow-x-auto" id="audit-table">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Date/Heure</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Administrateur</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Action</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Entité</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Détails</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">IP</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#0066FF]" />
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-slate-400">
                      Aucun log trouvé
                    </td>
                  </tr>
                ) : (
                  logs.map((log, index) => (
                    <tr 
                      key={log.id} 
                      className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors animate-fade-in"
                      style={{ animationDelay: `${index * 0.02}s` }}
                    >
                      <td className="py-3 px-4 text-sm text-slate-600 whitespace-nowrap">
                        {formatDate(log.timestamp)}
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium text-slate-900">{log.admin_email}</p>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {getActionBadge(log.action)}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs font-mono bg-slate-100 px-2 py-1 rounded">
                          {log.entity_type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 max-w-md truncate">
                        {log.details}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500 font-mono">
                        {log.ip_address || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default AuditLogsPage;
